const clean = value => String(value || "").trim()

export function normalizeLinkedRequirementConfig(source = {}) {
	return {
		...source,
		groupingOptions: (Array.isArray(source.groupingOptions) ? source.groupingOptions : []).map((row, index) => ({id: String(row.id || `group_${index}`), title: String(row.title || ""), mode: String(row.mode || "dimension")})),
		groupingId: String(source.groupingId || ""),
	}
}

export function normalizeLinkedRequirementPayload(payload = {}) {
	const worksheet = payload.stagedWorksheet || {}
	const matching = worksheet.linkedRequirements || {}
	return {
		...payload,
		stagedWorksheet: {
			...worksheet,
			linkedRequirements: {
				needs: (Array.isArray(matching.needs) ? matching.needs : []).map(row => ({
					id: String(row.id || ""), text: String(row.text || ""), evidence: String(row.evidence || ""), context: String(row.context || ""),
				})),
				consideredDimensionIds: (Array.isArray(matching.consideredDimensionIds) ? matching.consideredDimensionIds : []).map(String),
				unresolvedQuestion: String(matching.unresolvedQuestion || ""),
			},
		},
	}
}

export function updateLinkedRequirementPayload(payload = {}, change = {}) {
	const normalized = normalizeLinkedRequirementPayload(payload)
	const worksheet = normalized.stagedWorksheet
	const matching = worksheet.linkedRequirements
	return {...normalized, stagedWorksheet: {...worksheet, linkedRequirements: {...matching, ...change}}}
}

export function getLinkedRequirementView(payload = {}, config = {}) {
	const normalized = normalizeLinkedRequirementPayload(payload)
	const worksheet = normalized.stagedWorksheet
	const matching = worksheet.linkedRequirements
	const needsById = new Map(matching.needs.map(need => [need.id, need]))
	const included = Array.isArray(config.stages) ? config.stages : []
	const considered = new Set(matching.consideredDimensionIds)
	const toRows = stages => stages.flatMap(stage =>
		(stage.items || []).filter(item => clean(item.text)).map(item => {
			const linkedIds = Array.isArray(item.values?.needIds) ? item.values.needIds.map(String) : []
			const linkedNeeds = linkedIds.map(id => {
				const need = needsById.get(id)
				return need ? {...need, missing: !clean(need.text)} : {id, text: "", missing: true}
			})
			return {
				id: item.id, feature: item.text, dimensionId: stage.id, dimension: stage.title,
				context: clean(linkedNeeds[0]?.context), rationale: clean(item.values?.rationale), linkedIds, linkedNeeds,
				missingLink: !linkedNeeds.length || linkedNeeds.some(need => need.missing),
				missingRationale: !clean(item.values?.rationale),
			}
		}),
	)
	const rows = toRows((worksheet.stages || []).filter(stage => included.some(current => current.id === stage.id)))
	const archivedRows = toRows(worksheet.archivedStages || [])
	const matched = new Set(rows.flatMap(row => row.linkedNeeds.filter(need => !need.missing).map(need => need.id)))
	const coverage = included.map(stage => ({id: stage.id, title: stage.title, considered: considered.has(stage.id), requirementCount: rows.filter(row => row.dimensionId === stage.id).length}))
	const needs = matching.needs.map(need => ({...need, matched: matched.has(need.id)}))
	const activeGrouping = (config.linkedRequirements?.groupingOptions || []).find(option => option.id === config.linkedRequirements?.groupingId)
	const mode = activeGrouping?.mode || ""
	const groups = []
	for (const row of rows) {
		const title = mode === "context" ? (row.context || row.linkedNeeds[0]?.context || config.linkedRequirements?.contextLabel || "")
			: mode === "accessNeed" ? (row.linkedNeeds[0]?.text || config.linkedRequirements?.missingLinkLabel || "") : mode === "dimension" ? row.dimension : (config.linkedRequirements?.summaryTitle || "")
		let group = groups.find(item => item.title === title)
		if (!group) {group = {title, rows: []}; groups.push(group)}
		group.rows.push(row)
	}
	return {
		rows, archivedRows, groups, needs, coverage, grouping: activeGrouping?.title || "", groupingId: activeGrouping?.id || "",
		theme: String(worksheet.theme || ""),
		unresolvedQuestion: matching.unresolvedQuestion,
		missingRationale: rows.filter(row => row.missingRationale),
		missingLinks: rows.filter(row => row.missingLink),
		unmatchedNeeds: needs.filter(need => clean(need.text) && !need.matched),
		unconsidered: coverage.filter(row => !row.considered),
		narrowFocus: coverage.length > 1 && coverage.filter(row => row.requirementCount > 0).length === 1 && coverage.some(row => !row.considered),
		complete: rows.length > 0 && rows.every(row => !row.missingLink && !row.missingRationale),
	}
}

export function getLinkedRequirementChanges(current = {}, previous = {}) {
	const priorRows = new Map((previous.rows || []).map(row => [row.id, row]))
	const currentRows = new Map((current.rows || []).map(row => [row.id, row]))
	const changed = (current.rows || []).filter(row => {
		const prior = priorRows.get(row.id)
		return !prior || ["feature", "rationale", "context", "dimensionId"].some(key => row[key] !== prior[key]) || row.linkedIds.join("|") !== prior.linkedIds.join("|")
	}).map(row => ({...row, prior: priorRows.get(row.id) || null}))
	const removed = (previous.rows || []).filter(row => !currentRows.has(row.id))
	const previousNeeds = new Map((previous.needs || []).map(need => [need.id, need]))
	const changedNeeds = (current.needs || []).filter(need => !previousNeeds.has(need.id) || ["text", "evidence", "context"].some(key => need[key] !== previousNeeds.get(need.id)[key])).map(need => ({...need, prior: previousNeeds.get(need.id) || null}))
	return {changed, removed, retained: (current.rows || []).filter(row => !changed.some(item => item.id === row.id)), changedNeeds}
}

export function getEarlierLinkedRequirementResult(current = {}, candidates = []) {
	const timestamp = value => {
		if (value?.toMillis) return value.toMillis()
		if (Number.isFinite(value?.seconds)) return value.seconds * 1000
		if (Number.isFinite(value?._seconds)) return value._seconds * 1000
		const parsed = value instanceof Date ? value.getTime() : Date.parse(value || "")
		return Number.isFinite(parsed) ? parsed : null
	}
	const currentTime = timestamp(current.createdAt)
	return (Array.isArray(candidates) ? candidates : []).filter(result => !current.id || result.id !== current.id)
		.filter(result => currentTime === null || timestamp(result.createdAt) === null || timestamp(result.createdAt) < currentTime)
		.sort((a, b) => (timestamp(b.createdAt) || 0) - (timestamp(a.createdAt) || 0))[0] || null
}

export function validateLinkedRequirementCompletion(payload = {}, config = {}) {
	const view = getLinkedRequirementView(payload, config)
	if (!view.complete) throw new Error("Add a feature requirement with a documented need and rationale for every requirement")
}
